import React, { useMemo, useState } from 'react'
import { spacing, Text } from '@retailer-platform/shared-components'
import {
  ConfirmIcon,
  TrashIcon,
  EditIcon,
  RefundIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@instacart/ids-core'
import { IconButton } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { Input } from '../../../shared-styled-components/SharedStyledComponents'
import { type Rule } from '../RegexForm'
import { useDomainMessages } from '../../../../utils/domain/intl'

interface Props {
  rule: Rule
  onChange: (rule: Rule) => void
  onDelete: () => void
  isNew: boolean
  bannedMatches: string[]
  readOnly: boolean
  onOrderChange: (order: number) => void
  disableNext: boolean
  disablePrevious: boolean
}

const ActionRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: spacing.X8,
})

const Cell = styled.div({
  height: '62px',
  display: 'flex',
  alignItems: 'center',
})

export const RegexCardRulesGridRow = ({
  rule,
  onChange,
  onDelete,
  isNew,
  bannedMatches,
  readOnly,
  onOrderChange,
  disableNext,
  disablePrevious,
}: Props) => {
  const [isEditing, setIsEditing] = useState(isNew)
  const [ruleBuffer, setRuleBuffer] = useState(rule)

  const i18n = useDomainMessages({
    cancel:
      'catalogAdminDomain.attributeManagement.regex.form.mutation.cancelbutton.accessibleLabel',
    save: 'catalogAdminDomain.attributeManagement.regex.form.mutation.savebutton.accessibleLabel',
    moveUp: 'catalogAdminDomain.attributeManagement.regex.form.mutation.moveUp',
    moveDown: 'catalogAdminDomain.attributeManagement.regex.form.mutation.moveDown',
    edit: 'catalogAdminDomain.attributeManagement.regex.form.mutation.editbutton.accessibleLabel',
    delete:
      'catalogAdminDomain.attributeManagement.regex.form.mutation.deletebutton.accessibleLabel',
    ruleMatchErrorAlreadyExists:
      'catalogAdminDomain.attributeManagement.regex.form.rule.match.error.alreadyExists',
    ruleMatchErrorInvalidRegex:
      'catalogAdminDomain.attributeManagement.regex.form.rule.match.error.invalidRegex',
    ruleMatchErrorInvalidRegexLink:
      'catalogAdminDomain.attributeManagement.regex.form.rule.match.error.invalidRegex.link',
    ruleMatchErrorEmpty: 'catalogAdminDomain.attributeManagement.regex.form.rule.match.error.empty',
  })
  const validRegex = useMemo(() => {
    try {
      new RegExp(ruleBuffer.match)
      return true
    } catch (e) {
      return false
    }
  }, [ruleBuffer.match])
  const isBanned = useMemo(
    () => bannedMatches.includes(ruleBuffer.match),
    [bannedMatches, ruleBuffer.match]
  )

  const anythingChanged = useMemo(
    () => JSON.stringify(ruleBuffer) !== JSON.stringify(rule),
    [ruleBuffer, rule]
  )

  const viewActions = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={EditIcon}
          onPress={() => setIsEditing(true)}
          accessibleLabel={i18n.edit}
          disabled={readOnly}
        />
        <IconButton
          icon={TrashIcon}
          onPress={onDelete}
          accessibleLabel={i18n.delete}
          disabled={readOnly}
        />
      </ActionRow>
    ),
    [setIsEditing, onDelete, i18n.delete, i18n.edit, readOnly]
  )

  const editActions = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={RefundIcon}
          onPress={() => setIsEditing(false)}
          accessibleLabel={i18n.cancel}
          disabled={readOnly}
        />
        <IconButton
          icon={ConfirmIcon}
          disabled={!anythingChanged || isBanned || readOnly}
          onPress={() => {
            onChange(ruleBuffer)
            setIsEditing(false)
          }}
          accessibleLabel={i18n.save}
        />
      </ActionRow>
    ),
    [ruleBuffer, onChange, anythingChanged, i18n.cancel, i18n.save, isBanned, readOnly]
  )

  const newActions = useMemo(
    () => (
      <ActionRow>
        <IconButton icon={TrashIcon} onPress={onDelete} accessibleLabel={i18n.cancel} />
        <IconButton
          icon={ConfirmIcon}
          disabled={!anythingChanged || isBanned}
          onPress={() => {
            onChange(ruleBuffer)
          }}
          accessibleLabel={i18n.save}
        />
      </ActionRow>
    ),
    [ruleBuffer, onChange, anythingChanged, i18n.cancel, i18n.save, isBanned, onDelete]
  )

  const actions = useMemo(() => {
    if (isNew) {
      return newActions
    }
    return isEditing ? editActions : viewActions
  }, [isNew, isEditing, editActions, viewActions, newActions])

  const orderButtons = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={ArrowUpIcon}
          onPress={() => onOrderChange(-1)}
          accessibleLabel={i18n.moveUp}
          disabled={disablePrevious}
          style={{ marginLeft: '-12px' }}
        />
        <IconButton
          icon={ArrowDownIcon}
          onPress={() => onOrderChange(+1)}
          accessibleLabel={i18n.moveDown}
          disabled={disableNext}
        />
      </ActionRow>
    ),
    [onOrderChange, disableNext, disablePrevious, i18n.moveDown, i18n.moveUp]
  )

  return (
    <>
      {orderButtons}
      <Cell>
        {isEditing ? (
          <div style={{ width: '100%', alignSelf: 'flex-start' }}>
            <Input
              type="text"
              value={ruleBuffer.match}
              onChange={e => setRuleBuffer({ ...ruleBuffer, match: e.target.value })}
              style={{ width: '100%', alignSelf: 'flex-start' }}
              error={isBanned || !validRegex || ruleBuffer.match.length === 0}
            />
            {isBanned && (
              <Text size="small" styles={css({ color: 'red' })}>
                {i18n.ruleMatchErrorAlreadyExists}
              </Text>
            )}
            {!validRegex && (
              <Text size="small" styles={css({ color: 'red' })}>
                {i18n.ruleMatchErrorInvalidRegex}{' '}
                <a href="https://regex101.com/" target="_blank" rel="noopener noreferrer">
                  {i18n.ruleMatchErrorInvalidRegexLink}
                </a>
                .
              </Text>
            )}
            {ruleBuffer.match.length === 0 && (
              <Text size="small" styles={css({ color: 'red' })}>
                {i18n.ruleMatchErrorEmpty}
              </Text>
            )}
          </div>
        ) : (
          <Text size="small">{rule.match}</Text>
        )}
      </Cell>
      <Cell>
        {isEditing ? (
          <Input
            type="text"
            value={ruleBuffer.replace}
            onChange={e => setRuleBuffer({ ...ruleBuffer, replace: e.target.value })}
            style={{ alignSelf: 'flex-start' }}
          />
        ) : (
          <Text size="small">{rule.replace}</Text>
        )}
      </Cell>
      {actions}
    </>
  )
}
